import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const usageModeLabels: Record<string, string> = {
  instant: "Sofort verfügbar",
  request: "Auf Anfrage",
  later: "Später entscheiden",
};

const AdminPreLaunch = () => {
  const { user } = useAuth();

  const { data: hosts, isLoading: hostsLoading } = useQuery({
    queryKey: ["admin_hosts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pre_launch_hosts")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const { data: drivers, isLoading: driversLoading } = useQuery({
    queryKey: ["admin_drivers_waitlist"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("drivers_waitlist")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-5xl py-8">
        <Link to="/dashboard">
          <Button variant="ghost" size="sm" className="mb-4 gap-2">
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </Button>
        </Link>

        <h1 className="text-2xl font-bold text-foreground mb-1">Pre-Launch Daten</h1>
        <p className="text-sm text-muted-foreground mb-6">Übersicht aller Registrierungen</p>

        <Tabs defaultValue="hosts">
          <TabsList>
            <TabsTrigger value="hosts">Hosts ({hosts?.length ?? 0})</TabsTrigger>
            <TabsTrigger value="drivers">Fahrer ({drivers?.length ?? 0})</TabsTrigger>
          </TabsList>

          <TabsContent value="hosts" className="mt-4">
            {hostsLoading && <p className="text-sm text-muted-foreground">Lädt…</p>}
            {hosts?.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Host-Registrierungen.</p>}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="pb-2 pr-4 font-medium text-muted-foreground">Name</th>
                    <th className="pb-2 pr-4 font-medium text-muted-foreground">Telefon</th>
                    <th className="pb-2 pr-4 font-medium text-muted-foreground">Ort</th>
                    <th className="pb-2 pr-4 font-medium text-muted-foreground">Stecker</th>
                    <th className="pb-2 pr-4 font-medium text-muted-foreground">kW</th>
                    <th className="pb-2 pr-4 font-medium text-muted-foreground">Wallbox</th>
                    <th className="pb-2 pr-4 font-medium text-muted-foreground">Modus</th>
                    <th className="pb-2 pr-4 font-medium text-muted-foreground">Datum</th>
                  </tr>
                </thead>
                <tbody>
                  {hosts?.map((h) => (
                    <tr key={h.id} className="border-b border-border/50">
                      <td className="py-2.5 pr-4 font-medium text-foreground">{h.name}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{h.phone}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{h.address}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{h.connector_type}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{h.power_kw ?? "—"}</td>
                      <td className="py-2.5 pr-4">{h.has_wallbox ? "✓" : "✗"}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{usageModeLabels[h.usage_mode] ?? h.usage_mode}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{format(new Date(h.created_at), "dd.MM.yy", { locale: de })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="drivers" className="mt-4">
            {driversLoading && <p className="text-sm text-muted-foreground">Lädt…</p>}
            {drivers?.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Fahrer-Registrierungen.</p>}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="pb-2 pr-4 font-medium text-muted-foreground">Name</th>
                    <th className="pb-2 pr-4 font-medium text-muted-foreground">E-Mail</th>
                    <th className="pb-2 pr-4 font-medium text-muted-foreground">Ort</th>
                    <th className="pb-2 pr-4 font-medium text-muted-foreground">Ladeprobleme</th>
                    <th className="pb-2 pr-4 font-medium text-muted-foreground">Dringlichkeit</th>
                    <th className="pb-2 pr-4 font-medium text-muted-foreground">Datum</th>
                  </tr>
                </thead>
                <tbody>
                  {drivers?.map((d) => (
                    <tr key={d.id} className="border-b border-border/50">
                      <td className="py-2.5 pr-4 font-medium text-foreground">{d.name}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{d.email}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{d.location}</td>
                      <td className="py-2.5 pr-4">{d.had_charging_problems ? "Ja" : "Nein"}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{d.urgency}/5</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{format(new Date(d.created_at), "dd.MM.yy", { locale: de })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPreLaunch;
